import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const GROUPS = [
  {
    index: "01",
    kicker: "Deposits & banking",
    title: "Banking",
    tone: "light",
    items: ["Commercial Banks", "Rural Banks", "Savings & Loans", "Credit Unions"],
  },
  {
    index: "02",
    kicker: "Credit & origination",
    title: "Lending",
    tone: "light",
    items: [
      "Digital Lenders",
      "Mortgage Institutions",
      "Microfinance Institutions",
      "Asset Finance Companies",
      "Hire Purchase Companies",
    ],
  },
  {
    index: "03",
    kicker: "Protection & wealth",
    title: "Insurance & investment",
    tone: "light",
    items: ["Insurance Companies", "Investment Companies", "Pension Providers"],
  },
  {
    index: "04",
    kicker: "Digital infrastructure",
    title: "FinTech & payments",
    tone: "dark",
    items: ["FinTech Companies", "Payment Service Providers"],
  },
];

const TOTAL = GROUPS.reduce((sum, group) => sum + group.items.length, 0);

function Industries() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  let running = 0;
  const groups = GROUPS.map((group) => {
    const start = running;
    running += group.items.length;
    return { ...group, start };
  });

  return (
    <section
      id="industries"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="industries-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_45%_at_0%_0%,rgba(229,184,11,0.1),transparent_55%),radial-gradient(50%_40%_at_100%_10%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.div
          className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <header className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Industries we serve
            </p>
            <h2
              id="industries-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Our intelligent ecosystem
              <span className="mt-1 block">supports</span>
            </h2>
          </header>
          <p className="font-mono text-sm tracking-[0.18em] text-navy/40">
            {String(TOTAL).padStart(2, "0")} sectors · one platform
          </p>
        </motion.div>

        <motion.div
          className="mt-14 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-2"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: reduce ? 0 : 0.1 } },
          }}
        >
          {groups.map((group) => (
            <IndustryGroup key={group.title} group={group} variants={fade} reduce={reduce} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function IndustryGroup({ group, variants, reduce }) {
  const dark = group.tone === "dark";

  return (
    <motion.article
      className={`relative flex flex-col px-6 py-8 sm:px-8 sm:py-10 ${
        dark ? "bg-navy text-white" : "bg-white text-navy"
      }`}
      variants={variants}
    >
      {dark ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
          <div className="pointer-events-none absolute -right-12 top-8 h-40 w-40 rounded-full bg-royal/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 left-8 h-36 w-36 rounded-full bg-gold/10 blur-3xl" />
        </>
      ) : null}

      <div className="relative flex items-center justify-between gap-4">
        <p
          className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${
            dark ? "text-gold/90" : "text-navy/35"
          }`}
        >
          {group.kicker}
        </p>
        <span
          className={`font-mono text-[11px] font-semibold tracking-[0.2em] ${
            dark ? "text-white/35" : "text-navy/25"
          }`}
        >
          {group.index}
        </span>
      </div>
      <h3
        className={`relative mt-5 text-2xl font-semibold tracking-tight ${
          dark ? "" : "text-royal"
        }`}
      >
        {group.title}
      </h3>
      <p
        className={`relative mt-2 font-mono text-[11px] tracking-[0.16em] ${
          dark ? "text-white/40" : "text-navy/35"
        }`}
      >
        {String(group.items.length).padStart(2, "0")} sectors
      </p>

      <ul className="relative mt-8 flex-1">
        {group.items.map((item, i) => {
          const number = String(group.start + i + 1).padStart(2, "0");
          return (
            <motion.li
              key={item}
              className={`flex items-baseline gap-4 border-t py-3.5 first:border-t-0 first:pt-0 ${
                dark ? "border-white/10" : "border-navy/10"
              }`}
              initial={reduce ? false : { opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={
                reduce ? { duration: 0 } : { duration: 0.35, delay: 0.04 * i, ease: EASE }
              }
            >
              <span
                className={`w-6 shrink-0 font-mono text-[11px] font-semibold tracking-[0.14em] ${
                  dark ? "text-gold" : "text-navy/30"
                }`}
              >
                {number}
              </span>
              <span className={`text-[15px] leading-snug ${dark ? "text-white" : "text-navy/80"}`}>
                {item}
              </span>
            </motion.li>
          );
        })}
      </ul>

      {dark ? (
        <a
          href="#contact"
          className="relative mt-8 inline-flex min-h-11 w-fit cursor-pointer items-center gap-2 rounded-full bg-gold px-5 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
        >
          Talk to us
          <ArrowIcon />
        </a>
      ) : null}
    </motion.article>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Industries;
